/**
 * 동료가 합류할 때 보여 주는 이야기. 화면 전용 글이라 시뮬레이션과 무관하다.
 *
 * 사람이 합류하는 것은 전리품과 다르다. 이름을 가진 개체가 오는 순간인데, 예전에는
 * 결과 화면에 줄 하나로 지나갔다. 플레이한 사람이 "장수를 얻는 이벤트 씬"을 달라고 했다.
 *
 * 이야기는 기록(삼국사기 열전·본기)에 있는 일만 적는다. 대사는 지어낸 것이므로
 * 역사 속 말처럼 보이지 않게 유형(용장·지장·덕장)마다 하나씩, 게임 안의 목소리로 둔다.
 */
import { COMPANIONS } from '../sim/troops.ts';
import type { CompanionDef } from '../sim/troops.ts';

export interface CompanionLore {
  /** 이름 아래 붙는 한 줄. 누구였는가. */
  readonly who: string;
  /** 기록에 남은 일 두세 문장. */
  readonly story: string;
}

export const COMPANION_LORE: Readonly<Record<string, CompanionLore>> = {
  go_ondal: {
    who: '평원왕 때의 장군',
    story:
      // 온달전: 생김새가 우스꽝스럽고 해진 옷·떨어진 신으로 저잣거리를 오가니 사람들이 '바보 온달'이라 했다.
      // 처음에는 '가난하여 바보라 불렸다'로 적었는데, 가난은 어머니를 봉양하려 빌어먹었다는 대목이지 별명의
      // 까닭이 아니다(플레이한 사람이 짚었다).
      '생김새가 우스웠고 해진 옷차림으로 저잣거리를 오가 바보 온달이라 불렸다. 평강공주를 만나 말 타고 활 쏘는 법을 익혔고, ' +
      '낙랑 언덕의 사냥에서 으뜸을 차지해 이름을 알렸다. 뒤에 잃은 땅을 되찾겠다며 나섰다.',
  },
  go_euldudji: {
    who: '대무신왕의 좌보',
    story:
      '한나라 요동태수가 쳐들어와 성을 에워쌌다. 을두지는 연못의 잉어와 술을 적진에 보내 ' +
      '성 안에 물이 넉넉함을 보였고, 오래 버틸 수 없다고 여긴 적은 물러갔다.',
  },
  go_myeongnim: {
    who: '신대왕 때의 국상',
    story:
      '한나라 대군이 오자 싸우러 나가지 않고 들을 비운 채 성을 굳게 지켰다. ' +
      '굶주린 적이 물러갈 때 뒤를 쳐, 좌원에서 말 한 필 돌아가지 못하게 했다.',
  },
  bj_gyebaek: {
    who: '의자왕 때의 달솔',
    story:
      '신라와 당이 함께 쳐들어오자 결사대 오천을 이끌고 황산벌에 나섰다. ' +
      '열 배에 이르는 신라군 오만과 네 번 싸워 네 번 다 물리쳤다.',
  },
  bj_seongchung: {
    who: '의자왕 때의 좌평',
    story:
      '나라가 위태롭다고 거듭 간하다 옥에 갇혔다. 굶어 죽어 가면서도 글을 올려, ' +
      '적이 오면 육로는 탄현을, 물길은 기벌포를 막으라고 남겼다.',
  },
  bj_heukchi: {
    who: '백제의 달솔 겸 풍달군장',
    story:
      '백제가 무너진 뒤 임존성으로 가 흩어진 무리를 모았다. ' +
      '열흘 만에 삼만이 모였고, 이백여 성을 되찾았다.',
  },
  sl_jukjuk: {
    who: '대야성의 사지',
    story:
      // 백제본기는 성주 품석이 항복했다고, 죽죽 열전은 항복하려다 스스로 죽었다고 적는다.
      // 처음에는 "성주가 항복하자"로 한쪽 기록만 적었다. 둘이 겹치는 데까지만 적는다.
      '백제군이 대야성을 에워쌌다. 성주가 항복하려다 죽자 남은 군사를 모아 성문을 닫았다. ' +
      '항복을 권하는 말을 물리치고 성이 떨어질 때까지 싸웠다.',
  },
  sl_yusin: {
    who: '가야 왕족의 후손',
    story:
      '열다섯에 화랑이 되었다. 낭비성에서 스스로 적진에 뛰어들어 흔들리던 신라군을 일으켰고, ' +
      '뒤에 삼국이 하나로 묶이는 싸움의 한가운데에 섰다.',
  },
  sl_alcheon: {
    who: '선덕여왕 때의 장군',
    story:
      // 진덕왕이 죽은 뒤 신하들이 청한 것은 섭정이다. 처음에는 "화백 회의에서
      // 추대받았다"고 적어 왕위를, 그것도 회의의 형식까지 단정한 것처럼 읽혔다.
      '옥문곡에 숨어든 백제 군사 오백을 찾아 모두 쳐 없앴다. ' +
      '뒤에 여러 신하가 섭정을 청했으나 사양하고 김춘추를 천거했다.',
  },
};

/**
 * 결과 화면 앞에 맞이할 동료. 이번 전투에서 합류한 사람이 있고 아직 맞이하지 않았으면
 * 그 동료, 아니면 없다. 맞이한 뒤에 다시 부르면 결과로 가야 한다 — 장면을 또 띄우면
 * "맞이한다"를 눌러도 결과로 넘어가지 못하고 같은 장면만 돈다.
 */
export function companionToGreet(joined: string | null, greeted: boolean): CompanionDef | undefined {
  if (greeted || !joined) return undefined;
  return COMPANIONS.find((c) => c.id === joined);
}

/** 유형마다 합류하며 하는 한마디. 기록의 말이 아니라 게임 안의 목소리다. */
export const ARCHETYPE_LINE: Readonly<Record<string, string>> = {
  용장: '앞은 내가 연다. 뒤를 따라오기만 하라.',
  지장: '싸움은 먼저 무너지는 쪽이 진다. 우리는 늦게 무너지면 된다.',
  덕장: '한 명이라도 더 데리고 돌아가자. 그게 이기는 것이다.',
};
